import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon, Logo, Mark, BlobField, Button } from "../components/ui";
import { familyService } from "../services/familyService";
import { formatPhone, PHONE_DIGITS } from "../utils/format";
import { validateForm } from "../utils/validate";

export default function Register() {
  const navigate = useNavigate();
  const [centres, setCentres] = useState([]);
  const [form, setForm] = useState({
    parent_first_name: "",
    parent_last_name: "",
    primary_relationship: "",
    email: "",
    phone: "",
    centre_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [resubmitted, setResubmitted] = useState(false);

  useEffect(() => {
    familyService.getCentres().then((res) => setCentres(res?.data || [])).catch(() => {});
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    const next = name === "phone" ? formatPhone(value) : value;
    setForm((f) => ({ ...f, [name]: next }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (
      !validateForm(e.currentTarget, [
        { name: "parent_first_name", value: form.parent_first_name, label: "First name", required: true },
        { name: "parent_last_name", value: form.parent_last_name, label: "Last name", required: true },
        { name: "primary_relationship", value: form.primary_relationship, label: "Relationship to child", required: true },
        { name: "email", value: form.email, label: "Email", required: true, type: "email" },
        { name: "centre_id", value: form.centre_id, label: "Centre", required: true },
        { name: "phone", value: form.phone, label: "Mobile", type: "auMobile" },
      ])
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    setBusy(true);
    try {
      const centre = centres.find((c) => String(c.id) === String(form.centre_id));
      const res = await familyService.register({
        parent_first_name: form.parent_first_name.trim(),
        parent_last_name: form.parent_last_name.trim(),
        primary_relationship: form.primary_relationship.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        centre_id: form.centre_id,
        centre_name: centre?.name || "",
      });
      if (res?.success) {
        setResubmitted(!!res?.data?.resubmitted);
        setDone(true);
      } else {
        setError(res?.msg || "We couldn't submit your registration.");
      }
    } catch (err) {
      setError(err?.response?.data?.msg || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap login-wrap--register">
      <div className="login-hero on-dark">
        <BlobField />
        <div className="login-hero-inner">
          <div className="hero-brand">
            <Mark size={52} variant="amber" />
            <Logo variant="white" height={44} />
          </div>
          <div className="hero-spacer">
            <p className="eyebrow on-dark">Family Portal</p>
            <h1 className="display hero-title">
              Join your Stride<br />family space.
            </h1>
            <p className="hero-lead">
              Tell us a little about your family. Your centre will verify your details and
              email you a link to set your password.
            </p>
          </div>
          <p className="hero-tagline">Steps towards a bright future.</p>
        </div>
      </div>

      <div className="login-form-side">
        {done ? (
          <div className="login-card auth-done">
            <span className="icon-circle lg icon-center"><Icon name="check" className="ico-28" /></span>
            <h2 className="mt-24">{resubmitted ? "Request submitted again" : "Registration received"}</h2>
            <p className="muted mt-12">
              {resubmitted
                ? "We've received your updated details. Your centre will review your registration again and email you if approved."
                : "Thanks! Your registration is awaiting approval from your centre. Once approved, you'll receive an email with a link to set your password and sign in."}
            </p>
            <div className="mt-28">
              <Button variant="primary" block onClick={() => navigate("/login")}>Back to sign in</Button>
            </div>
          </div>
        ) : (
          <form className="login-card" onSubmit={submit} noValidate>
            <p className="eyebrow">Activate your account</p>
            <h2 className="auth-h2">Tell us about your family</h2>
            <p className="auth-intro">
              Each registration is verified by your centre before access is granted.
            </p>

            {error && <div className="alert-soft">{error}</div>}

            <div className="field-grid register-grid">
              <div>
                <label className="field-label">First name</label>
                <input
                  className="input"
                  name="parent_first_name"
                  value={form.parent_first_name}
                  onChange={onChange}
                  placeholder="e.g. Sarah"
                  required
                />
              </div>
              <div>
                <label className="field-label">Last name</label>
                <input
                  className="input"
                  name="parent_last_name"
                  value={form.parent_last_name}
                  onChange={onChange}
                  placeholder="e.g. Taylor"
                  required
                />
              </div>
            </div>

            <div className="form-field">
              <label className="field-label">Relationship to child</label>
              <input
                className="input"
                name="primary_relationship"
                value={form.primary_relationship}
                onChange={onChange}
                placeholder="e.g. Mother"
                required
              />
            </div>

            <div className="form-field">
              <label className="field-label">Email address</label>
              <input
                className="input"
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@email.com"
                required
              />
            </div>

            <div className="form-field">
              <label className="field-label">Mobile <span className="optional">(optional)</span></label>
              <input
                className="input"
                name="phone"
                type="tel"
                inputMode="numeric"
                value={form.phone}
                onChange={onChange}
                placeholder={`${PHONE_DIGITS} digits`}
              />
            </div>

            <div className="form-field form-field--centre">
              <label className="field-label">Your centre</label>
              <select className="select" name="centre_id" value={form.centre_id} onChange={onChange} required>
                <option value="">Select your centre</option>
                {centres.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <Button variant="primary" block type="submit" disabled={busy} iconRight={busy ? null : "chevronRight"}>
              {busy ? "Submitting…" : "Submit registration"}
            </Button>

            <hr className="divider divider-lg" />
            <p className="signin-alt">
              Already have access? <Link to="/login" className="link-strong">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
